import { S } from '../core/game';
import { bus } from '../core/bus';
import { dayId, type GameState, type ItemKey } from '../core/state';

/**
 * Money and consumer education (life package): allowance, piggy bank, needs vs wants,
 * impulse ads and the wish list. Pure state logic lives here; the dialogs are in lifeMoney.ts.
 */

export const ALLOWANCE = 100;
/** Mom's Sunday "matching": 10 % of what is in the piggy bank. */
export const INTEREST = 0.1;

/** Every price the shops use is tagged: needs keep you healthy and safe, wants are nice extras. */
export const NEED_WANT: Partial<Record<ItemKey, 'need' | 'want'>> = {
  water: 'need',
  riceBall: 'need',
  umbrella: 'need',
  orange: 'need',
  sandwich: 'need',
  book: 'need',
  tea: 'want',
  juice: 'want',
  candy: 'want',
  cookies: 'want',
  cake: 'want',
  strawberries: 'want',
  cocoa: 'want',
  toy: 'want',
  keychain: 'want',
  flowers: 'want',
  scarf: 'want',
};

export function needOrWant(item: ItemKey): 'need' | 'want' {
  return NEED_WANT[item] ?? 'want';
}

/** Extra life-package state, kept on the save object (save.ts keeps unknown top-level fields). */
export interface LifeExtra {
  /** Gift fund from the allowance split. */
  gift: number;
  /** Spending budget for `budgetWeek`. */
  budget: number;
  budgetWeek: number;
  /** Needs / wants spent in `spendWeek`. */
  spend: { week: number; need: number; want: number };
  /** Worksheet results (latest last). */
  works: { kind: string; grade: 'A' | 'B' | 'C'; score: number; week: number }[];
  /** Last week with an exhibition. */
  exhibitWeek: number;
  /** Wish-list prices: item → the ad price, and the day it was added. */
  wishes: { item: ItemKey; price: number; day: number }[];
  /** Need/want questions answered right (all time). */
  nwRight: number;
  /** Totals for the wallet donut (all time). */
  total: { need: number; want: number };
}

export function life(s: GameState = S()): LifeExtra {
  const x = s as GameState & { life?: Partial<LifeExtra> };
  const l = (x.life ??= {});
  l.gift ??= 0;
  l.budget ??= 0;
  l.budgetWeek ??= 0;
  l.spend ??= { week: s.week, need: 0, want: 0 };
  l.works ??= [];
  l.exhibitWeek ??= 0;
  l.wishes ??= [];
  l.nwRight ??= 0;
  l.total ??= { need: 0, want: 0 };
  return l as LifeExtra;
}

export function dayNo(s: GameState = S()): number {
  return s.week * 3 + s.dayIndex;
}

/** Record a purchase in the per-day log and this week's totals. */
export function logSpend(kind: 'need' | 'want', price: number, s: GameState = S()): void {
  if (price <= 0) return;
  const d = dayNo(s);
  const row = (s.money.log[d] ??= { need: 0, want: 0 });
  row[kind] += price;
  const l = life(s);
  if (l.spend.week !== s.week) l.spend = { week: s.week, need: 0, want: 0 };
  l.spend[kind] += price;
  l.total[kind] += price;
  // Keep the log short: the last ~30 game days.
  for (const k of Object.keys(s.money.log)) if (Number(k) < d - 30) delete s.money.log[Number(k)];
  bus.emit('change');
}

export function weekSpend(s: GameState = S()): { need: number; want: number; budget: number } {
  const l = life(s);
  const sp = l.spend.week === s.week ? l.spend : { need: 0, want: 0 };
  return { need: sp.need, want: sp.want, budget: l.budgetWeek === s.week ? l.budget : 0 };
}

/** Split presets for the allowance (spend / piggy / gift), always summing to the allowance. */
export const SPLITS = {
  balanced: [50, 30, 20],
  saver: [30, 60, 10],
  spender: [80, 10, 10],
} as const;

export function validSplit(spend: number, piggy: number, gift: number, total = ALLOWANCE): boolean {
  return [spend, piggy, gift].every((v) => Number.isInteger(v) && v >= 0) && spend + piggy + gift === total;
}

/** Saturday morning, once per game week. */
export function allowanceDue(s: GameState = S()): boolean {
  return dayId(s) === 'sat' && s.money.lastAllowanceWeek < s.week;
}

export function payAllowance(spend: number, piggy: number, gift: number, s: GameState = S()): boolean {
  if (!validSplit(spend, piggy, gift)) return false;
  s.money.lastAllowanceWeek = s.week;
  s.coins += spend;
  s.money.piggy += piggy;
  const l = life(s);
  l.gift += gift;
  l.budget = spend;
  l.budgetWeek = s.week;
  bus.emit('change');
  return true;
}

export function interestFor(piggy: number): number {
  if (piggy <= 0) return 0;
  return Math.max(1, Math.round(piggy * INTEREST));
}

/** Sunday morning, once per week: Mom's 10 % matching. Returns the amount added. */
export function payInterest(s: GameState = S()): number {
  const key = `life.interest.${s.week}`;
  if (dayId(s) !== 'sun' || s.forever[key]) return 0;
  s.forever[key] = true;
  const add = interestFor(s.money.piggy);
  s.money.piggy += add;
  bus.emit('change');
  return add;
}

export function deposit(n: number, s: GameState = S()): boolean {
  if (!(n > 0) || s.coins < n) return false;
  s.coins -= n;
  s.money.piggy += n;
  bus.emit('change');
  return true;
}

export function withdraw(n: number, s: GameState = S()): boolean {
  if (!(n > 0) || s.money.piggy < n) return false;
  s.money.piggy -= n;
  s.coins += n;
  bus.emit('change');
  return true;
}

/** Pay a price, from the gift fund first when `useGift` (gift shop). Returns false if short. */
export function pay(price: number, useGift = false, s: GameState = S()): boolean {
  const l = life(s);
  const fromGift = useGift ? Math.min(l.gift, price) : 0;
  if (s.coins < price - fromGift) return false;
  l.gift -= fromGift;
  s.coins -= price - fromGift;
  bus.emit('change');
  return true;
}

export function canPay(price: number, useGift = false, s: GameState = S()): boolean {
  return s.coins + (useGift ? life(s).gift : 0) >= price;
}

// --- Impulse ads and the wish list -----------------------------------------------------------

export const AD_ITEMS: readonly { item: ItemKey; price: number }[] = [
  { item: 'toy', price: 25 },
  { item: 'keychain', price: 15 },
  { item: 'cookies', price: 20 },
  { item: 'cake', price: 60 },
  { item: 'scarf', price: 45 },
];

export interface AdOffer {
  item: ItemKey;
  /** The "was" price the ad shows (inflated, to anchor you). */
  was: number;
  /** Today's "limited" price — really just the normal price. */
  now: number;
}

export function makeAd(rnd: () => number = Math.random): AdOffer {
  const a = AD_ITEMS[Math.floor(rnd() * AD_ITEMS.length) % AD_ITEMS.length];
  return { item: a.item, was: Math.round((a.price * 1.6) / 5) * 5, now: a.price };
}

export function addWish(item: ItemKey, price: number, s: GameState = S()): void {
  const l = life(s);
  if (l.wishes.some((w) => w.item === item)) return;
  l.wishes.push({ item, price, day: dayNo(s) });
  if (!s.money.wish.includes(item)) s.money.wish.push(item);
  bus.emit('change');
}

export type WishOutcome = { item: ItemKey; kind: 'cheaper'; price: number; was: number } | { item: ItemKey; kind: 'forgot' };

/**
 * The next day: each wish either got cheaper (the "limited" offer wasn't) or you notice
 * you don't really need it. Clears the list and returns what happened.
 */
export function resolveWishes(rnd: () => number = Math.random, s: GameState = S()): WishOutcome[] {
  const l = life(s);
  const today = dayNo(s);
  const due = l.wishes.filter((w) => w.day < today);
  l.wishes = l.wishes.filter((w) => w.day >= today);
  s.money.wish = l.wishes.map((w) => w.item);
  return due.map((w) => (rnd() < 0.5 ? { item: w.item, kind: 'cheaper', price: Math.max(1, Math.round(w.price * 0.7)), was: w.price } : { item: w.item, kind: 'forgot' }));
}
